import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

interface PolicyPreviewProps {
  policy: {
    id: string;
    title: string;
    department: string | null;
    version: number;
    status: string;
    created_at: string;
    effective_date: string | null;
    next_renewal_date: string | null;
    content: string;
    created_by: string;
    creator?: {
      name: string;
      designation?: string;
    };
  };
  activityLog?: any[];
  isOpen: boolean;
  onClose: () => void;
}

const PolicyPreview = ({ policy, activityLog = [], isOpen, onClose }: PolicyPreviewProps) => {
  const [printContent, setPrintContent] = useState<string>("");

  // Prepare HTML content for printing
  useEffect(() => {
    if (isOpen && policy) {
      const htmlContent = generatePrintContent(policy, activityLog);
      setPrintContent(htmlContent);
    }
  }, [isOpen, policy, activityLog]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle>Policy Preview</DialogTitle>
          <div className="flex gap-2 mt-4">
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print / Save as PDF
            </Button>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-6">
          <div 
            className="bg-white text-black print-container"
            dangerouslySetInnerHTML={{ __html: printContent }}
            style={{ maxWidth: '8.5in', margin: '0 auto' }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const generatePrintContent = (policy: any, activityLog: any[]): string => {
  const getDepartmentDisplay = (dept: string | null) => {
    if (!dept) return 'General';
    return dept.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Cover Page HTML
  const coverPage = `
    <div style="page-break-after: always; min-height: 100vh; display: flex; flex-direction: column; justify-content: center; align-items: center; border: 2px solid #000; padding: 80px 60px; margin-bottom: 40px;">
      <div style="text-align: center;">
        <h1 style="font-size: 42px; font-weight: bold; margin-bottom: 60px; color: #1a1a1a;">POLICY DOCUMENT</h1>
        <h2 style="font-size: 32px; font-weight: 600; margin-bottom: 40px; color: #2a2a2a; line-height: 1.4;">${policy.title}</h2>
        <div style="margin-top: 80px; padding-top: 40px; border-top: 3px solid #000;">
          <p style="font-size: 18px; margin: 8px 0;">
            <strong>Department:</strong> ${getDepartmentDisplay(policy.department)}
          </p>
          <p style="font-size: 18px; margin: 8px 0;">
            <strong>Version:</strong> ${policy.version}
          </p>
          <p style="font-size: 18px; margin: 8px 0;">
            <strong>Effective Date:</strong> ${policy.effective_date ? format(new Date(policy.effective_date), "MMMM d, yyyy") : 'Not set'}
          </p>
          <p style="font-size: 18px; margin: 8px 0; margin-top: 20px;">
            <strong>Document Status:</strong> <span style="text-transform: uppercase;">${policy.status.replace('_', ' ')}</span>
          </p>
        </div>
      </div>
    </div>
  `;

  // Policy Information Table
  const infoTable = `
    <div style="page-break-after: always; margin: 40px 0;">
      <h2 style="font-size: 24px; font-weight: bold; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">POLICY INFORMATION</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold; width: 30%;">Document Title</td>
          <td style="padding: 12px 8px;">${policy.title}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Department</td>
          <td style="padding: 12px 8px;">${getDepartmentDisplay(policy.department)}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Version</td>
          <td style="padding: 12px 8px;">${policy.version}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Status</td>
          <td style="padding: 12px 8px; text-transform: uppercase;">${policy.status.replace('_', ' ')}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Created Date</td>
          <td style="padding: 12px 8px;">${format(new Date(policy.created_at), "MMMM d, yyyy")}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Effective Date</td>
          <td style="padding: 12px 8px;">${policy.effective_date ? format(new Date(policy.effective_date), "MMMM d, yyyy") : 'Not set'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Next Renewal Date</td>
          <td style="padding: 12px 8px;">${policy.next_renewal_date ? format(new Date(policy.next_renewal_date), "MMMM d, yyyy") : 'Not set'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="padding: 12px 8px; font-weight: bold;">Created By</td>
          <td style="padding: 12px 8px;">${policy.creator?.name || 'Unknown'} ${policy.creator?.designation ? `(${policy.creator.designation})` : ''}</td>
        </tr>
      </table>
    </div>
  `;

  // Policy Content
  const contentSection = `
    <div style="page-break-after: always; margin: 40px 0;">
      <h2 style="font-size: 24px; font-weight: bold; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">POLICY CONTENT</h2>
      <div style="margin-top: 20px; line-height: 1.8; font-size: 14px;">
        ${policy.content}
      </div>
    </div>
  `;

  // Activity Log Table
  const activityTable = `
    <div style="margin: 40px 0;">
      <h2 style="font-size: 24px; font-weight: bold; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">DOCUMENT HISTORY</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px;">
        <thead>
          <tr style="background-color: #f5f5f5; border-bottom: 2px solid #000;">
            <th style="padding: 10px 8px; text-align: left; font-weight: bold;">Date</th>
            <th style="padding: 10px 8px; text-align: left; font-weight: bold;">Action</th>
            <th style="padding: 10px 8px; text-align: left; font-weight: bold;">Performed By</th>
            <th style="padding: 10px 8px; text-align: left; font-weight: bold;">Details</th>
          </tr>
        </thead>
        <tbody>
          ${activityLog.map((activity, index) => {
            const detailText = activity.details ? 
              (typeof activity.details === 'string' ? activity.details : JSON.stringify(activity.details)) : 
              '-';
            return `
              <tr style="border-bottom: 1px solid #ddd;">
                <td style="padding: 10px 8px;">${format(new Date(activity.created_at), "MMM d, yyyy HH:mm")}</td>
                <td style="padding: 10px 8px;">${activity.action_type.replace('_', ' ').toUpperCase()}</td>
                <td style="padding: 10px 8px;">${activity.performed_by_name || 'System'}</td>
                <td style="padding: 10px 8px;">${detailText.substring(0, 100)}${detailText.length > 100 ? '...' : ''}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  `;

  return `
    <style>
      @media print {
        body { margin: 0; }
        .print-container { box-shadow: none !important; }
        @page { margin: 1in; }
      }
      table { page-break-inside: avoid; }
      tr { page-break-inside: avoid; page-break-after: auto; }
    </style>
    ${coverPage}
    ${infoTable}
    ${contentSection}
    ${activityTable}
  `;
};

export default PolicyPreview;
